import { useEffect, useRef } from 'react';

function useUpdateDraft(values, errors, additionalDeps) {
    const deps = [values, errors, ...additionalDeps];

    const draftRef = useRef();
    useEffect(() => {
        draftRef.current = {
            values,
            errors,
            additionalDeps,
        };
    }, deps);

    return draftRef;
}

// Initializing interval once the component mounts,
// which saves the progress at every 10 secs
// returning cleanup function which would be called
// when component is unmounted which will clear
// interval.
function useSaveDraft(draftRef, autoSaveCallback, autoSaveInterval) {
    useEffect(() => {
        const interval = setInterval(() => {
            // note that effects/any function defined inside functional
            // components would read the prop/state from the time it was created in.
            // hence would contain values from that render. Since the interval is being created
            // after the mount, it will have values from first render.
            // using draftRef to have the latest values.
            autoSaveCallback(
                draftRef.current.values,
                draftRef.current.errors,
                draftRef.current.additionalDeps
            );
        }, autoSaveInterval);
        return () => clearInterval(interval);
    }, []);
}

/**
 *
 * callback will provide updated deps array via ref.
 * use these variable if updated values are required
 * instead of values from the global scope,
 * since those will have values when this hook/effect was initialized.
 *
 * The reason that we are getting updated values via ref
 * and not by updating the effect directly via deps is that internally
 * we are creating an interval function which is created when component is
 * mounted and cleared back when component is unmounted and never updated in
 * between.
 *
 * If we start using deps array to update that intervalFn via effect,
 * it might result in delaying draft saving indefinitely in
 * certain scenarios. For example, if someone navigates to next page,
 * just before draft call is about to be made, interval would be created,
 * and freshly created interval object would wait 10 seconds from its creation
 * time. Now again when draft call is about to be made, click on previous button
 * will further delay the draft saving.
 *
 * @param autoSaveCallback
 * @param values
 * @param errors
 * @param additionalDeps
 * @param autoSaveInterval
 */
export function useAutoSaveDraft(
    autoSaveCallback,
    values,
    errors,
    additionalDeps = [],
    autoSaveInterval = 2000
) {
    const draftRef = useUpdateDraft(values, errors, additionalDeps);
    useSaveDraft(draftRef, autoSaveCallback, autoSaveInterval);
}
